import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ResponseError } from "app/services/fetchService";

import type { MethodName, Tag } from "./apiClient";
import { apiClient } from "./apiClient";
import type { Args, Data } from "./queryTypes";
import type { InvalidateQueriesData } from "./utils";
import { getInvalidationQueryKeys } from "./utils";

type MutationOptions<T extends Tag, M extends MethodName<T>> = Omit<
    UseMutationOptions<Data<T, M>, ResponseError, Args<T, M>>,
    "mutationFn"
> & {
    invalidateQueries?: InvalidateQueriesData;
};

/**
 * @experimental
 */
export const useApiMutation = <T extends Tag, M extends MethodName<T>>(
    tag: T,
    methodName: M,
    { invalidateQueries, onSuccess, ...options }: MutationOptions<T, M> = {},
) => {
    const queryClient = useQueryClient();

    return useMutation<Data<T, M>, ResponseError, Args<T, M>>({
        ...options,
        mutationFn: (args) => apiClient[tag][methodName](...args) as Promise<Data<T, M>>,
        onSuccess: (...args) => {
            onSuccess?.(...args);

            if (!invalidateQueries) {
                return;
            }

            getInvalidationQueryKeys(invalidateQueries).forEach((queryKey) => {
                if (queryKey) {
                    void queryClient.invalidateQueries({ queryKey });
                }
            });
        },
    });
};
